import type { LabeledGraphicPoint } from '@vedalib/editor/lib/elements'
import { createItem } from '@vedalib/editor/lib/elements/elements/LabeledGraphic/constants'
import type { FileUsage } from '@vedalib/editor/lib/files'
import { FileUsageTypeEnum } from '@vedalib/editor/lib/files'
import React from 'react'
import { FileTypeEnum } from 'utils'

import FloatingPanel from 'components/Editor/FloatingPanel'
import PanelIconImage from 'components/Editor/FloatingPanel/PanelIconImage'
import { genElementValueField } from 'components/Editor/Toolbar/NodeForm/genElementValueField'
import Padding from 'components/Editor/Toolbar/Padding'
import { FieldArray, FieldColorPicker, FieldFile, FieldSelect, FormField } from 'components/formKit'
import { TileButton } from 'components/uiKit/Button'
import { Typography } from 'components/uiKit/Typography'
import { useBrandColorOptions } from 'services/Branding/fieldConfigs/useFieldConfigs'
import { t } from 'services/Translation'
import { getFileByUsage } from 'utils/files'
import { chainName } from 'utils/form'

import LabeledGraphicFormValueItem from './LabeledGraphicFormValueItem'
import type { LabeledGraphicValueFormType } from './labeledGraphicTypes'

const TYPE = genElementValueField({
  as: FieldSelect<'number' | 'image' | 'withoutIcon'>,
  name: 'type',
  label: t('input.label.type'),
  defaultValue: 'image',
  props: {
    fluid: true,
    options: [
      { label: t('input.option.number'), value: 'number' },
      { label: t('input.option.icons'), value: 'image' },
      { label: t('input.option.withoutIcon'), value: 'withoutIcon' },
    ],
  },
})

const FILE = genElementValueField({
  name: 'image',
  as: FieldFile<FileUsage>,
  props: {
    fileType: FileTypeEnum.IMAGE,
    nullable: true,
    preview: true,
    showSource: true,
    hasObjectFit: true,
    hasAccessibility: true,
  },
})

const OVERLAY = genElementValueField({
  name: 'overlay',
  as: FieldColorPicker<string | null>,
  label: t('input.label.overlay'),
  defaultValue: null,
  props: { labeled: true, resettable: true },
  useConfigs: [useBrandColorOptions],
})

const ITEMS = genElementValueField({
  name: 'items',
  as: FieldArray<LabeledGraphicPoint>,
  rules: [{ max: 200 }],
  props: {
    defaultItem: createItem(),
    RenderItem: LabeledGraphicFormValueItem,
    min: 1,
  },
})

const LabeledGraphicForm: LabeledGraphicValueFormType = ({ name, element: { value } }) => {
  const file = getFileByUsage(value.image, FileUsageTypeEnum.image)

  return (
    <Padding bottom bottomSpace gap sides>
      <FloatingPanel
        header={<Typography weight='medium'>{t('elements.labeledGraphic.form.image')}</Typography>}
        panel={
          <Padding gap>
            <FormField config={chainName(name, FILE)} />
            <FormField config={chainName(name, OVERLAY)} />
          </Padding>
        }
      >
        {({ open }) => (
          <TileButton active={open} icon='otherAdd' name='settings' styleType='stroke'>
            <PanelIconImage file={value.image} icon='otherBlock' />
            <Typography rows={1} styleType='hint' tooltip={file?.name}>
              {file?.name || t('elements.labeledGraphic.form.image')}
            </Typography>
          </TileButton>
        )}
      </FloatingPanel>
      <Typography weight='medium'>{t('elements.chart.form.points')}</Typography>
      <Typography styleType='hint'>{t('elements.labeledGraphic.form.pointDescription')}</Typography>
      <FormField config={chainName(name, TYPE)} />
      <FormField config={chainName(name, ITEMS)} />
    </Padding>
  )
}
export default React.memo(LabeledGraphicForm) as typeof LabeledGraphicForm
